import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { img } from "../constants/assets";

const partnerStatConfig = [
  { icon: "counter1.svg", value: 10000, suffix: "+", decimals: 0 },
  { icon: "counter2.svg", value: 1, suffix: "M+", decimals: 0 },
  { icon: "counter3.svg", value: 200, suffix: "+", decimals: 0 },
  { icon: "counter4.svg", value: 650, suffix: "+", decimals: 0 },
  { icon: "counter5.svg", value: 300, suffix: "%", decimals: 0 },
  { icon: "counter6.svg", value: 99.9, suffix: "%", decimals: 1 },
];

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function formatCount(current, stat) {
  const n =
    stat.decimals > 0
      ? current.toFixed(stat.decimals)
      : Math.floor(current).toLocaleString("en-US");

  if (stat.suffix === "M+") {
    return `${n}M+`;
  }

  return `${n}${stat.suffix}`;
}

function CounterItem({ stat, label, animate }) {
  const [display, setDisplay] = useState(() => formatCount(0, stat));

  useEffect(() => {
    if (!animate) return;

    const duration = 2000;
    let frameId;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const current = stat.value * easeOutCubic(progress);
      setDisplay(formatCount(current, stat));
      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      } else {
        setDisplay(formatCount(stat.value, stat));
      }
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [animate, stat]);

  return (
    <div className="col-6 col-md-4 col-lg-2">
      <article className="partner-counter-item">
        <div className="partner-counter-icon">
          <img src={img(stat.icon)} alt="" aria-hidden="true" />
        </div>
        <p className="partner-counter-value">{display}</p>
        <p className="partner-counter-label">{label}</p>
      </article>
    </div>
  );
}

function PartnerCounters() {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const [animate, setAnimate] = useState(false);
  const counterLabels = t("partners.counterLabels", { returnObjects: true });

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      setAnimate(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimate(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="partner-counters-section py-50" ref={sectionRef}>
      <div className="container">
        <h2 className="partner-counters-title text-center">
          {t("partners.countersTitle")}
        </h2>
        <div className="row g-4 g-lg-5 mt-2 justify-content-center">
          {partnerStatConfig.map((stat, index) => (
            <CounterItem
              key={counterLabels[index]}
              stat={stat}
              label={counterLabels[index]}
              animate={animate}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default PartnerCounters;
